<script setup lang="ts">
import type { contentVariants, quotesVariants, Testimonial } from '.';
import type { HTMLAttributes } from 'vue';

import { BrokerAvatarInfo } from '@/components/BrokerAvatarInfo';
import { cn } from '@/utils';

import {
	brokerInfo,
	brokerName,
	companyName,
	content,
	footer,
	testimonialGiver,
	testimonialGiverSubtitle
} from '.';

interface TestimonialProps {
	class?: HTMLAttributes['class'];
	contentClass?: HTMLAttributes['class'];
	footerClass?: HTMLAttributes['class'];
	brokerInfoClass?: HTMLAttributes['class'];
	variant?: contentVariants['variant'];
	testimonial: Testimonial;
}

const props = withDefaults(defineProps<TestimonialProps>(), {
	variant: 'dark',
	brokerInfoClass: 'h-8 w-8'
});

const getImageSrc = useImage();

const brokerSrc = computed(() =>
	getImageSrc(props.testimonial.brokerImage || '', {
		format: 'webp',
		width: 32,
		height: 32
	})
);

const companySrc = computed(() =>
	getImageSrc(props.testimonial.companyImage || '', {
		format: 'webp',
		width: 32,
		height: 32
	})
);
</script>

<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div
		:class="
			cn(
				'flex h-full flex-col gap-y-3 border-none bg-transparent p-6 shadow-none lg:gap-y-5',
				props.class
			)
		"
	>
		<div
			class="relative h-[250px] border-l-8 border-beige p-0 lg:h-[340px]"
			data-testid="header"
		>
			<NuxtImg
				:src="props.testimonial.image"
				width="320"
				height="340"
				sizes="xs:280px sm:320px"
				densities="x1"
				format="webp"
				alt="Testifier photo"
				loading="lazy"
				class="size-full object-cover"
			/>
			<div
				:class="
					cn(
						testimonialGiver({
							heightVariant: !!testimonial.propertyType ? 'full' : 'onlyName'
						})
					)
				"
				data-testid="testimonialGiver"
			>
				<p
					class="quotes text-base leading-tight text-primary before:absolute before:right-0 before:top-[-3px] before:h-[24px] before:w-[28px] before:-translate-y-1/2 before:bg-beige lg:text-xl"
					data-testid="testimonialGiverTitle"
				>
					{{ testimonial.author }}
				</p>
				<small
					v-if="testimonial.propertyType"
					:class="cn(testimonialGiverSubtitle({ variant }))"
					data-testid="testimonialGiverSubtitle"
				>
					Comprou uma {{ testimonial.propertyType.name }}
				</small>
			</div>
		</div>

		<div :class="cn(content({ variant }), contentClass)" data-testid="content">
			{{ testimonial.quote }}
		</div>

		<div :class="cn(footer({ variant }), footerClass)" data-testid="footer">
			<p class="text-xs leading-[200%]">Atendimento</p>
			<div :class="cn(brokerInfo({ variant }))">
				<BrokerAvatarInfo
					:avatar-class="cn('', brokerInfoClass)"
					sizes="linear"
					:border="false"
					:info-classes="{
						wrapper: 'text-start justify-between',
						brokerName: cn(brokerName({ variant })),
						companyName: cn(companyName({ variant }))
					}"
					:broker-src="brokerSrc"
					:company-src="companySrc"
					class="gap-8"
				>
					<template #broker>{{ testimonial.brokerName }}</template>
					<template #company>{{ testimonial.companyName }}</template>
				</BrokerAvatarInfo>
			</div>
		</div>
	</div>
</template>
